import React, { useState, useEffect } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { CheckCircle2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { DIVISIONS, MARITIME_REGIONS } from "@/data/divisions";
import { RFQ_FORM } from "@/constants/testIds";

const INITIAL = {
  company_name: "",
  contact_name: "",
  contact_email: "",
  contact_phone: "",
  maritime_region: "",
  divisions: [],
  project_scope: "",
  target_start_date: "",
};

export default function Contact() {
  const [params] = useSearchParams();
  const preDivision = params.get("division");
  const preEntity = params.get("entity");

  const [form, setForm] = useState(INITIAL);
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    if (preDivision && DIVISIONS.find((d) => d.id === preDivision)) {
      setForm((f) => ({ ...f, divisions: [preDivision] }));
    }
    if (preEntity) {
      setForm((f) => ({
        ...f,
        project_scope:
          f.project_scope || `Interested in engaging specifically with ${preEntity}. Scope:\n`,
      }));
    }
  }, [preDivision, preEntity]);

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleDivision = (id) =>
    setForm((f) =>
      f.divisions.includes(id)
        ? { ...f, divisions: f.divisions.filter((x) => x !== id) }
        : { ...f, divisions: [...f.divisions, id] }
    );

  const submit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    try {
      await api.post("/rfq", form);
      setDone(true);
      setForm(INITIAL);
      toast.success("RFQ submitted. A tender-desk lead will be in touch.");
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  if (done) {
    return (
      <div className="max-w-[900px] mx-auto px-6 lg:px-10 py-32 text-center">
        <div className="inline-flex w-16 h-16 bg-[#FF5722] items-center justify-center mb-6">
          <CheckCircle2 size={30} className="text-white" />
        </div>
        <h2 className="font-display text-4xl md:text-5xl tracking-tight mb-4">RFQ received.</h2>
        <p className="text-white/70 max-w-xl mx-auto">
          A senior tender-desk manager reviews every project inquiry within 24 hours. Expect a
          direct response with an initial engagement plan.
        </p>
        <button className="btn-secondary mt-8" onClick={() => setDone(false)} data-testid="rfq-submit-another">
          Submit another RFQ
        </button>
      </div>
    );
  }

  return (
    <div className="text-white">
      <section className="border-b border-white/10">
        <div className="max-w-[1400px] mx-auto px-6 lg:px-10 pt-24 pb-14">
          <div className="eyebrow mb-6">Contact · Project Tendering</div>
          <h1 className="font-display font-black text-5xl md:text-7xl leading-[0.95] tracking-tighter max-w-4xl">
            One inquiry.<br />
            <span className="text-[#FF5722]">One hundred entities on standby.</span>
          </h1>
          <p className="mt-8 max-w-2xl text-lg text-white/70 leading-relaxed">
            Submit an RFQ (Request for Proposal) below. The Group&rsquo;s master tender desk routes
            each request to the exact subsidiaries required for scope, region and timeline.
          </p>
        </div>
      </section>

      <section className="max-w-[1200px] mx-auto px-6 lg:px-10 py-16 grid grid-cols-1 lg:grid-cols-12 gap-8">
        <div className="lg:col-span-4">
          <div className="border border-white/10 bg-[#0C1322] p-6 lg:sticky lg:top-24">
            <div className="eyebrow mb-4">Direct Lines</div>
            <ul className="space-y-4 text-sm">
              <li>
                <div className="text-white/50 text-xs font-mono uppercase tracking-widest">Tender Desk</div>
                <div className="text-white">tenders@billtechgroup.com</div>
              </li>
              <li>
                <div className="text-white/50 text-xs font-mono uppercase tracking-widest">HR / Careers</div>
                <div className="text-white">careers@billtechgroup.com</div>
              </li>
              <li>
                <div className="text-white/50 text-xs font-mono uppercase tracking-widest">Media</div>
                <div className="text-white">press@billtechgroup.com</div>
              </li>
              <li>
                <div className="text-white/50 text-xs font-mono uppercase tracking-widest">24/7 Duty Line</div>
                <div className="text-white">+1 (713) 555-0179</div>
              </li>
            </ul>
            <div className="mt-8 pt-6 border-t border-white/10">
              <div className="eyebrow mb-3">Operating Regions</div>
              <div className="flex flex-wrap gap-1.5">
                {MARITIME_REGIONS.slice(0, 8).map((r) => (
                  <span key={r} className="text-[10px] font-mono uppercase tracking-widest border border-white/15 text-white/70 px-2 py-1">
                    {r}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>

        <form onSubmit={submit} className="lg:col-span-8 border border-white/10 bg-[#0C1322] p-8 lg:p-10">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <Field label="Company name *">
              <input required value={form.company_name} onChange={(e) => set("company_name", e.target.value)} data-testid={RFQ_FORM.companyName} className={inputCls} placeholder="e.g. Chevron, ExxonMobil" />
            </Field>
            <Field label="Contact name *">
              <input required value={form.contact_name} onChange={(e) => set("contact_name", e.target.value)} data-testid={RFQ_FORM.contactName} className={inputCls} />
            </Field>
            <Field label="Contact email *">
              <input required type="email" value={form.contact_email} onChange={(e) => set("contact_email", e.target.value)} data-testid={RFQ_FORM.contactEmail} className={inputCls} />
            </Field>
            <Field label="Contact phone">
              <input value={form.contact_phone} onChange={(e) => set("contact_phone", e.target.value)} data-testid={RFQ_FORM.contactPhone} className={inputCls} />
            </Field>
            <Field label="Target maritime region *">
              <select required value={form.maritime_region} onChange={(e) => set("maritime_region", e.target.value)} data-testid={RFQ_FORM.region} className={inputCls}>
                <option value="">Select region</option>
                {MARITIME_REGIONS.map((r) => <option key={r} value={r}>{r}</option>)}
              </select>
            </Field>
            <Field label="Target start date">
              <input type="date" value={form.target_start_date} onChange={(e) => set("target_start_date", e.target.value)} data-testid={RFQ_FORM.startDate} className={inputCls} />
            </Field>
          </div>

          <div className="mt-8">
            <div className="text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-3">
              Core requirement (select all that apply)
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {DIVISIONS.map((d) => (
                <label
                  key={d.id}
                  className={`flex items-start gap-3 p-3 border cursor-pointer transition-colors ${
                    form.divisions.includes(d.id)
                      ? "border-[#FF5722] bg-[#FF5722]/10"
                      : "border-white/10 hover:border-white/30"
                  }`}
                >
                  <input
                    type="checkbox"
                    checked={form.divisions.includes(d.id)}
                    onChange={() => toggleDivision(d.id)}
                    data-testid={RFQ_FORM.divisionCheckbox(d.id)}
                    className="mt-1 accent-[#FF5722]"
                  />
                  <div>
                    <div className="text-sm font-bold text-white">{d.name.replace("Billtech ", "")}</div>
                    <div className="text-xs text-white/60">{d.tagline}</div>
                  </div>
                </label>
              ))}
            </div>
          </div>

          <div className="mt-8">
            <Field label="Technical scope / description *" full>
              <textarea
                required
                rows={7}
                minLength={10}
                value={form.project_scope}
                onChange={(e) => set("project_scope", e.target.value)}
                data-testid={RFQ_FORM.projectScope}
                className={inputCls}
                placeholder="Describe the project, water depth, timeline, deliverables, and any regulatory context..."
              />
            </Field>
          </div>

          <div className="mt-8 pt-6 border-t border-white/10 flex flex-col md:flex-row items-start md:items-center justify-between gap-4">
            <div className="text-xs text-[#94A3B8] font-mono uppercase tracking-widest max-w-md">
              For document uploads (technical sheets, tender packs), reply to the confirmation
              email you&rsquo;ll receive.
            </div>
            <button type="submit" disabled={submitting} className="btn-primary" data-testid={RFQ_FORM.submit}>
              {submitting ? "Submitting..." : "Submit RFQ"}
            </button>
          </div>
        </form>
      </section>
    </div>
  );
}

const inputCls =
  "w-full bg-[#060A14] border border-white/10 px-4 py-3 text-sm text-white placeholder:text-white/40 focus:border-[#FF5722] focus:ring-1 focus:ring-[#FF5722] outline-none";

function Field({ label, children, full }) {
  return (
    <label className={`block ${full ? "md:col-span-2" : ""}`}>
      <span className="block text-[11px] font-mono uppercase tracking-[0.2em] text-white/60 mb-2">
        {label}
      </span>
      {children}
    </label>
  );
}
